import {
  conspiracyOrganizedRecords,
  corporateRecords,
  criminalRecordsMenu,
  destructionRecords,
  drugRecords,
  harassmentRecords,
  otherRecords,
  sexOffenseRecords,
  societalRecords,
  theftOffenseRecords,
  violentRecords
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/constants";
import {
  FieldName,
  Rule
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import {
  offenseSelectionLabel,
  offenseSelectionNames
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/criminalOffenseSelection";
import { formatTitleFirstWord } from "utils/formatTitle";
import { readableFieldName, readableFieldSubtitle } from "./constants";

export const NO_DATA_RECEIVED = "No data received";

/** The rule had nothing to compare with its threshold: no applicant had a value,
 * e.g. the credit report came back with no hit or no score. The API stores
 * `computed_value: null` for such rules. */
export const hasNoRuleData = (rule: Rule) =>
  !rule.skipped && rule.computed_value == null;

interface RuleApplicantResult {
  applicant_id: number;
  /** null when no data was received for the applicant */
  value: number | null;
  result?: boolean;
}

/** Per-applicant results, followed by the applicants with no data. The API leaves
 * those out of `per_applicant_results`, so they are read from `applicant_values`. */
export const getRuleApplicantResults = (rule: Rule): RuleApplicantResult[] => {
  const results = rule.per_applicant_results ?? [];
  const applicantIdsWithResult = new Set(
    results.map(({ applicant_id }) => applicant_id)
  );
  const applicantsWithoutData = (rule.applicant_values ?? [])
    .filter(
      ({ applicant_id, value }) =>
        value == null && !applicantIdsWithResult.has(applicant_id)
    )
    .map(({ applicant_id }) => ({ applicant_id, value: null }));

  return [...results, ...applicantsWithoutData];
};

const fieldsWithSubcategory = [
  FieldName.FelonyRecords,
  FieldName.MisdemeanorRecords,
  FieldName.OtherCriminalRecords,
  FieldName.AllCriminalRecords
];

export const formatThreshold = (rule: Rule) => {
  if (
    [FieldName.SexOffenderCheck, FieldName.IncomeDocumentFraudCheck].includes(
      rule.field_name
    )
  )
    return rule.threshold === 1 || rule.threshold === "1" ? "Yes" : "No";
  else {
    if (rule.threshold === "true") return "Yes";
    if (rule.threshold === "false") return "No";
    return rule.threshold || "-";
  }
};

export const formatConditionTitle = (
  field_name?: FieldName,
  subCategories?: string[]
) => {
  if (!field_name) return "-";

  if (field_name === FieldName.OfacRecords)
    return "Sanctions (OFAC) convictions";

  const hasSubcategory = fieldsWithSubcategory.find((key) =>
    field_name.includes(key)
  );

  if (hasSubcategory) {
    const subcategories = {
      ...drugRecords(hasSubcategory),
      ...violentRecords(hasSubcategory),
      ...sexOffenseRecords(hasSubcategory),
      ...theftOffenseRecords(hasSubcategory),
      ...destructionRecords(hasSubcategory),
      ...harassmentRecords(hasSubcategory),
      ...societalRecords(hasSubcategory),
      ...conspiracyOrganizedRecords(hasSubcategory),
      ...corporateRecords(hasSubcategory),
      ...otherRecords(hasSubcategory)
    };

    const menuItems = criminalRecordsMenu(true)[hasSubcategory];
    const currentSubcategory =
      offenseSelectionNames(menuItems, field_name, subCategories) ||
      offenseSelectionLabel(menuItems, field_name, subCategories) ||
      subcategories[field_name] ||
      "";

    return (
      `${formatTitleFirstWord(hasSubcategory).replaceAll("records", "convictions")}` +
      "   " +
      `${currentSubcategory.toLowerCase()}`
    );
  }

  return (
    readableFieldName[field_name as string] ||
    formatTitleFirstWord(field_name).replaceAll("records", "convictions")
  );
};

export const formatConditionSubTitle = (field_name: FieldName) => {
  return readableFieldSubtitle[field_name];
};

export const formatApplicantValue = (rule: Rule, value: string | number) => {
  if (
    [FieldName.SexOffenderCheck, FieldName.IncomeDocumentFraudCheck].includes(
      rule.field_name
    )
  )
    return value === 1 || value === "1" ? "Yes" : "No";
  else return String(value);
};

const parseNumericRuleValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatNumberValue = (value: number) => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

export const formatDisplayedComputedValue = (
  computedValue: number | undefined,
  threshold: string | number | undefined
) => {
  const parsedComputed = parseNumericRuleValue(computedValue);
  if (parsedComputed !== null) return formatNumberValue(parsedComputed);

  const parsedThreshold = parseNumericRuleValue(threshold);
  if (parsedThreshold !== null) return "0";

  return "-";
};
